import Phaser from 'phaser';
import { Chest } from './Chest';
// LootSpawner - handles spawning loot (potions) and pickup/drop logic
// Integrates with theme.items loaded via Utils (itemsById in Game scene)

const DEFAULT_DENOMINATIONS = [50, 10, 5, 1];
const DENOMINATION_TINTS = {
  1: 0xFCD34D,
  5: 0x34D399,
  10: 0x60A5FA,
  50: 0xA855F7,
};
const CHAIN_TINTS = {
  solana: 0x14B8A6,
  sui: 0x8B5CF6,
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const ensureInventoryEntryMetadata = (entry) => {
  if (!entry) return entry;
  entry.itemId = entry.itemId ?? entry.item_id ?? null;
  entry.item_id = entry.itemId;
  entry.itemType = entry.itemType ?? entry.item_type ?? null;
  entry.item_type = entry.itemType;
  entry.equipmentSlot = entry.equipmentSlot ?? entry.equipment_slot ?? null;
  entry.equipment_slot = entry.equipmentSlot;
  entry.slotNumber = entry.slotNumber ?? entry.slot_number ?? null;
  entry.slot_number = entry.slotNumber;
  entry.iconKey = entry.iconKey ?? entry.icon_key ?? null;
  entry.icon_key = entry.iconKey;

  const quantity = Math.max(
    0,
    toNumber(entry.quantity ?? entry.qty ?? entry.amount, 0)
  );
  entry.qty = quantity;
  entry.quantity = quantity;

  entry.initialQty = Math.max(0, toNumber(entry.initialQty, quantity));
  entry.originalQty = Math.max(0, toNumber(entry.originalQty, entry.initialQty));
  entry.lootQty = Math.max(0, toNumber(entry.lootQty, 0));
  entry.consumedOriginal = Math.max(0, toNumber(entry.consumedOriginal, 0));
  entry.consumedLoot = Math.max(0, toNumber(entry.consumedLoot, 0));
  return entry;
};

const MAX_LOOT_DROP_STACK = 3;

const getEligibleDeathDropCount = (entry) => {
  if (!entry) return 0;
  const totalQty = Math.max(0, toNumber(entry.qty ?? entry.quantity, 0));
  if (totalQty <= 0) return 0;

  const lootQty = Math.max(0, toNumber(entry.lootQty, 0));
  if (lootQty > 0) {
    const capped = Math.min(lootQty, MAX_LOOT_DROP_STACK);
    return Math.max(1, Math.min(totalQty, capped));
  }

  const slotNumber = Number(entry.slotNumber ?? entry.slot_number ?? -1);
  const slotKey = (entry.equipmentSlot ?? entry.equipment_slot ?? '').toString().toLowerCase();
  const inContainerSlot = slotKey.includes('container');

  if (inContainerSlot && slotNumber === 0) {
    return Math.min(totalQty, 1);
  }

  return 0;
};

const createLootInventoryEntry = (data) => {
  const quantity = 1;
  const entry = {
    itemId: data.id,
    item_id: data.id,
    itemType: data.itemType || 'consumable',
    item_type: data.itemType || 'consumable',
    qty: quantity,
    quantity,
    equipmentSlot: data.equipmentSlot ?? null,
    equipment_slot: data.equipmentSlot ?? null,
    slotNumber: null,
    slot_number: null,
    iconKey: data.iconKey || null,
    icon_key: data.iconKey || null,
    themeId: data.themeId || data.theme_id || null,
    theme_id: data.themeId || data.theme_id || null,
    initialQty: 0,
    originalQty: 0,
    lootQty: quantity,
    consumedOriginal: 0,
    consumedLoot: 0,
  };
  return entry;
};

export class LootSpawner {
  constructor(scene) {
    this.scene = scene;
    this.loot = []; // array of Phaser.Images with lootData { id, type, tier, effect, iconKey }
    this.chests = []; // array of Chest instances
    this.enabled = true;
    this.spawnRadiusPadding = 100; // keep away from edges
    this.poisonDots = []; // hazards: { node, x, y, radius, damage, owner }
    this.prizePoolConfig = null;
  }

  // Returns a random potion item definition from scene.itemsById (theme items)
  // Filters out tiered potions (potion_*_t1, potion_*_t2, etc.) and only includes active items
  getRandomPotionDef() {
    try {
      // Prefer itemsById (from theme) over itemsData (may be stale)
      const itemsById = this.scene.itemsById;
      if (itemsById && itemsById.size > 0) {
        const potions = [];
        itemsById.forEach((itemDef, itemId) => {
          if (!itemDef || typeof itemDef !== 'object') return;
          
          // Skip tiered potions (potion_*_t1, potion_*_t2, etc.)
          if (itemId && /_t[1-5]$/.test(itemId)) return;
          
          // Only include potion types
          const itemType = itemDef.type;
          if (itemType === 'healing' || itemType === 'mana' || itemType === 'strength' || itemType === 'poison' || itemType === 'ammo') {
            potions.push(itemDef);
          }
        });
        
        if (potions.length > 0) {
          const idx = Math.floor(Math.random() * potions.length);
          return potions[idx];
        }
      }
      
      // Fallback to itemsData if itemsById not available
      const items = this.scene.itemsData || [];
      const potions = items.filter(i => {
        if (!i || typeof i !== 'object') return false;
        
        // Skip tiered potions
        const itemId = i.id || i.itemId || '';
        if (itemId && /_t[1-5]$/.test(itemId)) return false;
        
        // Only include potion types
        const itemType = i.type;
      return itemType === 'healing' || itemType === 'mana' || itemType === 'strength' || itemType === 'poison' || itemType === 'ammo';
      });
      
      if (!potions.length) return null;
      const idx = Math.floor(Math.random() * potions.length);
      return potions[idx];
    } catch (_) { return null; }
  }

  setPrizePoolConfig(config) {
    if (!config) {
      this.prizePoolConfig = null;
      return;
    }
    try {
      this.prizePoolConfig = JSON.parse(JSON.stringify(config));
    } catch (_) {
      this.prizePoolConfig = Array.isArray(config?.tokens) ? { tokens: [...config.tokens] } : null;
    }
  }

  // Spawn N random potions on the battlefield and any configured prize pool tokens
  spawnInitial(count = 20) {
    if (!this.enabled) return;
    this.ensureLists();
    // Spawn random potions
    const countOverride = this.scene?.sessionConfig?.initialTokenDropCount;
    const potionCount = Number.isFinite(countOverride) ? countOverride : count;
    for (let i = 0; i < potionCount; i++) this.spawnOneRandom();
    // Also scatter configured prize pool tokens or fallback tokens
    try {
      const tokens = Array.isArray(this.prizePoolConfig?.tokens) ? this.prizePoolConfig.tokens : [];
      if (tokens.length) {
        this.spawnPrizePoolTokens(tokens);
      } else {
        const tokenCount = 250;
        this.spawnRandomTokens(tokenCount);
      }
    } catch (error) {
      console.warn('[LootSpawner] Failed spawning prize pool tokens', error);
    }
  }

  ensureLists() {
    this.scene.worldPickups = this.scene.worldPickups || [];
  }

  spawnOneRandom() {
    if (!this.enabled) return null;
    const def = this.getRandomPotionDef();
    console.log('[LootSpawner] def:', def);
    if (!def || !def.iconKey) return null;
    //const bounds = this.scene.gameManager?.worldBounds || this.scene.cameras.main.worldView;
    const bounds = this.scene.gameManager?.spawnBoundsInitial || this.scene.gameManager?.worldBounds || this.scene.cameras.main.worldView;
    
    const pad = this.spawnRadiusPadding;
    const x = Phaser.Math.Between(bounds.x + pad, bounds.right - pad);
    const y = Phaser.Math.Between(bounds.y + pad, bounds.bottom - pad);
    return this.spawnAt(def, x, y);
  }

  spawnAt(itemDef, x, y) {
    try {
      const baseScale = 0.30;
      const iconKey = itemDef.iconKey;
      //console.log('[LootSpawner] spawning loot at:', x, y, iconKey);
      const img = this.scene.add.image(x, y, iconKey);
      img.setDepth(3);
      img.setScale(baseScale * (this.scene.objectScale || 1));
      //rotate the loor 45 degrees counterclockwise
      img.rotation = -Math.PI / 4;
      //img.rotation = Phaser.Math.Between(0, Math.PI * 2);
      //console.log('[LootSpawner] itemDef:', itemDef);
      img.lootData = {
        id: itemDef.id,
        type: itemDef.type,
        themeItemId: itemDef.id,
        loot_type: itemDef.loot_type || 'item',
        tier: itemDef.tier,
        effect: itemDef.effect || {},
        iconKey: iconKey
      };

      //have the item gently float up and down
      this.scene.tweens.add({
        targets: img,
        y: y + 10,
        duration: 1000,
        ease: 'easeInOut',
        repeat: -1,
        yoyo: true
      });

      //make the item a colored light source
      this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, itemDef.color || 0x000000);
      //make the light source a different color for each item
      switch (itemDef.type) {
        case 'strength':
          this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, 0x00ff00);
          break;
        case 'endurance':
          this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, 0x0000ff);
          break;
        case 'coordination':
          this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, 0xff0000);
          break;
        case 'quickness':
          this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, 0x00ffff);
          break;
        case 'focus':
          this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, 0xffffff);
          break;
        case 'self':
          this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, 0x000000);
          break;
        case 'healing':
          this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, 0xff0000);
          break;
        case 'mana':
          this.scene.lights.addLight(x, y, 100, 0x000000, 1.0, 0x0000ff);
          break;
        default:
          // No special lighting for unknown item types
          break;
      }

      this.loot.push(img);
      this.scene.worldPickups.push(img);
      if (this.scene.uiCamera && this.scene.uiCamera.ignore) this.scene.uiCamera.ignore(img);
      return img;
    } catch (_) {
      return null;
    }
  }

  // --- TOKEN (coin) spawns -------------------------------------------------

  spawnTokenAt(x, y, amount = 10, options = {}) {
    try {
      const baseScale = 0.60;
      const s = this.scene.add.sprite(x, y, 'spin-coin');
      s.setDepth(3);
      s.setScale(baseScale * (this.scene.objectScale || 1));
      s.play('spin-coin-anim');
      const tint = this.resolveTokenTint(options?.denomination, options?.chain, options?.tint);
      if (tint) {
        s.setTint(tint);
      }
      // Gentle bobbing
      this.scene.tweens.add({ targets: s, y: y + 6, duration: 1000, ease: 'Sine.inOut', repeat: -1, yoyo: true });
      // Token lootData for pickup (include token symbol)
      const tokenSymbol = options?.tokenSymbol || this.scene?.tokenSymbol || 'SOULWARS';
      const chain = options?.chain || null;
      s.lootData = {
        loot_type: 'token',
        amount,
        iconKey: 'spin-coin',
        tokenSymbol,
        chain,
        denomination: options?.denomination || null,
      };
      this.loot.push(s);
      this.scene.worldPickups.push(s);
      if (this.scene.uiCamera && this.scene.uiCamera.ignore) this.scene.uiCamera.ignore(s);
      return s;
    } catch (_) { return null; }
  }

  spawnRandomToken() {
    //const bounds = this.scene.gameManager?.worldBounds || this.scene.cameras.main.worldView;
    const bounds = this.scene.gameManager?.spawnBoundsInitial || this.scene.gameManager?.worldBounds || this.scene.cameras.main.worldView;

    const pad = this.spawnRadiusPadding;
    const x = Phaser.Math.Between(bounds.x + pad, bounds.right - pad);
    const y = Phaser.Math.Between(bounds.y + pad, bounds.bottom - pad);
    
    // Randomly pick a denomination with weighted probabilities
    // Higher values are rarer (50 = 5%, 10 = 15%, 5 = 30%, 1 = 50%)
    const rand = Math.random();
    let amount;
    if (rand < 0.05) {
      amount = 50; // 5% chance
    } else if (rand < 0.20) {
      amount = 10; // 15% chance
    } else if (rand < 0.50) {
      amount = 5; // 30% chance
    } else {
      amount = 1; // 50% chance
    }
    
    return this.spawnTokenAt(x, y, amount, { denomination: amount });
  }

  spawnRandomTokens(count = 6) {
    for (let i = 0; i < count; i++) this.spawnRandomToken();
  }

  spawnTokensBurstAt(x, y, count = 6) {
    const baseRadius = 35;
    for (let i = 0; i < count; i++) {
      // Use random angles instead of evenly spaced for better Y distribution
      const angle = Math.random() * Math.PI * 2;
      
      // Vary radius for more spread
      const radius = baseRadius * (0.7 + Math.random() * 1.3);
      
      // Much more Y variation - prioritize vertical spread
      const xSpread = Math.cos(angle) * radius * (0.5 + Math.random() * 1.0);
      const ySpread = Math.sin(angle) * radius * (1.0 + Math.random() * 3.0); // 1x to 4x multiplier for Y
      
      // Additional random Y offset for even more vertical variation
      const extraYOffset = (Math.random() - 0.5) * 40; // ±20 pixels extra
      
      const dx = x + xSpread;
      const dy = y + ySpread + extraYOffset;
      
      // Spawn token - it will have a bobbing animation that uses original y
      const s = this.spawnTokenAt(x, y, 10, {});
      if (!s) continue;
      try {
        // Stop any existing tweens on this sprite (like the bobbing animation)
        this.scene.tweens.killTweensOf(s);
        
        // Move to final position with tween
        this.scene.tweens.add({
          targets: s,
          x: dx,
          y: dy,
          duration: 400,
          ease: 'Power2.easeOut',
          onComplete: () => {
            // Ensure the sprite stays at the final position
            if (s && s.active) {
              s.x = dx;
              s.y = dy;
              
              // Restart bobbing animation centered on the new Y position
              this.scene.tweens.add({ 
                targets: s, 
                y: dy + 6, 
                duration: 1000, 
                ease: 'Sine.inOut', 
                repeat: -1, 
                yoyo: true 
              });
            }
          }
        });
      } catch (_) {}
    }
  }

  // Called from Game.update similarly to checkFoodCollection
  checkLootCollection() {
    const players = this.scene.players || [];
    for (let p = 0; p < players.length; p++) {
      const player = players[p];
      if (!player || !player.isAlive) continue;
      for (let i = this.loot.length - 1; i >= 0; i--) {
        const loot = this.loot[i];
        if (!loot || !loot.active) { this.loot.splice(i, 1); continue; }
        // Skip chests - they're handled separately via combat
        if (loot.lootData?.loot_type === 'token_chest') continue;
        const dist = Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, loot.x, loot.y);
        if (dist < 25) {
          this.onPickup(player, loot);
          this.loot.splice(i, 1);
          try { loot.destroy(); } catch (_) {}
        }
      }
    }
    
    // Clean up destroyed chests
    this.chests = this.chests.filter(chest => chest && !chest.isDestroyed);
  }

  onPickup(player, lootImage) {
    
    const data = lootImage.lootData || {};
    //console.log('[LootSpawner] onPickup data:', data);
    player.inventory = player.inventory || [];
    player.loot = player.loot || [];

    // Handle token pickup immediately (chests are handled via combat, not pickup)
    if (data.loot_type === 'token') {
      const amt = Math.max(0, data.amount || 0);
      player.tokens = (player.tokens || 0) + amt;
      // Aggregate into a single token entry in player.loot
      try {
        player.loot = player.loot || [];
        const existing = player.loot.find(e => ((e.lootType === 'token') || (e.loot_type === 'token')) && (!data.tokenSymbol || e.tokenSymbol === data.tokenSymbol));
        if (existing) {
          existing.qty = (existing.qty || 0) + amt;
          existing.itemId = existing.itemId || 'token-coin';
          existing.notes = existing.notes || 'Token';
          if (data.tokenSymbol && !existing.tokenSymbol) existing.tokenSymbol = data.tokenSymbol;
          if (data.chain && !existing.chain) existing.chain = data.chain;
        } else {
          const objectToPickup = {
            lootType: 'token',
            itemId: 'token-coin',
            qty: amt,
            themeItemId: null,
            notes: 'Token',
            tokenSymbol: data.tokenSymbol || 'SOULWARS',
            chain: data.chain || null,
          };
          //console.log('[LootSpawner] objectToPickup:', objectToPickup);
          player.loot.push(objectToPickup);
        }
      } catch (_) {}
      if (this.scene.showEmote) this.scene.showEmote(player, 'coin');
      if (this.scene && typeof this.scene.refreshSidebarForPlayer === 'function') {
        try { this.scene.refreshSidebarForPlayer(player); } catch (_) {}
      }
      return;
    }
    const itemDef = (this.scene.itemsById && this.scene.itemsById.get) ? this.scene.itemsById.get(data.id) : null;
    const isAmmo = itemDef?.type === 'ammo' || data.type === 'ammo' || itemDef?.type === 'arrow';
    if (isAmmo) {
      const amount = Number(itemDef?.effect?.arrows ?? data.effect?.arrows ?? 0);
      if (player.addArrows && amount > 0) {
        const added = player.addArrows(amount);
        if (added > 0 && this.scene.showEmote) this.scene.showEmote(player, '🏹');
        if (this.scene && typeof this.scene.refreshSidebarForPlayer === 'function') {
          try { this.scene.refreshSidebarForPlayer(player); } catch (_) {}
        }
      }
      return;
    }
    // Loot items are kept in player.loot, NOT added to player.inventory during the match
    // Stack if same id and stackable
    //console.log('[LootSpawner] itemDef:', itemDef);
    const stackable = itemDef?.stackable === 1 || itemDef?.stackable === true;
    
    // Find or create loot entry (loot stays separate from inventory)
    const lootExisting = player.loot.find(e => e.itemId === data.id);
    if (lootExisting) {
      lootExisting.qty = (lootExisting.qty || 1) + 1;
    } else {
      const objectToPickup = {
        lootType: 'item',
        itemId: data.id,
        qty: 1,
        themeItemId: data.themeItemId,
        notes: 'Item',
        iconKey: data.iconKey,
        itemType: itemDef?.type || null
      };
      player.loot.push(objectToPickup);
    }

    if (this.scene && typeof this.scene.refreshSidebarForPlayer === 'function') {
      try { this.scene.refreshSidebarForPlayer(player); } catch (_) {}
    }

    // Removed console.log statements for performance - these were called on every loot pickup
    // console.log('[LootSpawner] player.inventory:', player.name, player.heroId, player.inventory);
    // console.log('[LootSpawner] player.loot:', player.name, player.heroId, player.loot);

    // Optional feedback
    if (this.scene.showEmote) this.scene.showEmote(player, '🧪');

    // Consider immediate use based on context and strategy
    try {
      const def = (this.scene.itemsById && this.scene.itemsById.get) ? this.scene.itemsById.get(data.id) : null;
      if (def && typeof player.considerUsingPickedItem === 'function') {
        player.considerUsingPickedItem(def);
      }
    } catch (_) {}
  }

  // Drop all items from a player at a location
  dropInventoryAt(player, x, y) {
    const inv = Array.isArray(player?.inventory) ? player.inventory : [];
    if (!inv.length) return;
    const spread = 18;
    let angle = Math.random() * Math.PI * 2;
    inv.forEach(entry => {
      const def = (this.scene.itemsById && this.scene.itemsById.get) ? this.scene.itemsById.get(entry.itemId) : null;
      if (!def) return;
      const count = getEligibleDeathDropCount(entry);
      if (count <= 0) return;
      for (let c = 0; c < count; c++) {
        const dx = x + Math.cos(angle) * spread * (1 + c * 0.5);
        const dy = y + Math.sin(angle) * spread * (1 + c * 0.5);
        this.spawnAt(def, dx, dy);
        angle += (Math.PI * 2) / (count + 1);
      }
    });

    //clear the loot array
    player.loot = [];
  }

  // Drop with probability per individual item (e.g., 0.25 = 25%)
  dropInventoryWithChanceAt(player, x, y, probability = 0.25) {
    const inv = Array.isArray(player?.inventory) ? player.inventory : [];
    if (!inv.length || probability <= 0) return;
    const spread = 18;
    let angle = Math.random() * Math.PI * 2;
    inv.forEach(entry => {
      let def = (this.scene.itemsById && this.scene.itemsById.get) ? this.scene.itemsById.get(entry.itemId || entry.item_id) : null;
      if (!def) return;
      const count = getEligibleDeathDropCount(entry);
      if (count <= 0) return;
      for (let c = 0; c < count; c++) {
        if (Math.random() <= probability) {
          const dx = x + Math.cos(angle) * spread * (1 + c * 0.5);
          const dy = y + Math.sin(angle) * spread * (1 + c * 0.5);
          def.deathLoot = true;
          def.droppedBy = player?.name ? player.hero.name : 'Unknown' 
          this.spawnAt(def, dx, dy);

          //remove the item from the loot array
          player.loot = player.loot.filter(e => e.itemId !== def.id);
        }
        angle += (Math.PI * 2) / (count + 1);
      }
    });
  }

  // Spawn a poison hazard at location; damages other players on contact once
  spawnPoisonDot(x, y, effect = {}, owner = null) {
    try {
      const radius = 18 * (this.scene.objectScale || 1);
      const damage = Math.max(1, Math.floor(effect.dot || 3));
      const ttlMs = 9000; // lifetime
      const node = this.scene.add.graphics();
      node.fillStyle(0x22cc22, 0.85);
      node.fillCircle(x, y, radius);
      node.setDepth(4);
      if (this.scene.uiCamera && this.scene.uiCamera.ignore) this.scene.uiCamera.ignore(node);
      const hazard = { node, x, y, radius, damage, owner, born: Date.now(), ttlMs, hitWallets: new Set() };
      this.poisonDots.push(hazard);
      return hazard;
    } catch (_) {
      return null;
    }
  }

  // Check for players stepping into poison hazards
  checkPoisonCollisions() {
    const players = this.scene.players || [];
    for (let i = this.poisonDots.length - 1; i >= 0; i--) {
      const h = this.poisonDots[i];
      if (!h || !h.node) { this.poisonDots.splice(i, 1); continue; }
      // TTL
      if (Date.now() > (h.born + h.ttlMs)) {
        try { h.node.destroy(); } catch (_) {}
        this.poisonDots.splice(i, 1);
        continue;
      }
      for (let p = 0; p < players.length; p++) {
        const pl = players[p];
        if (!pl || !pl.isAlive) continue;
        if (h.owner && (pl === h.owner)) continue; // owner immune
        const key = pl.walletAddress || pl.name || p;
        if (h.hitWallets.has(key)) continue; // already hit
        const d = Phaser.Math.Distance.Between(pl.sprite.x, pl.sprite.y, h.x, h.y);
        if (d <= h.radius) {
          console.log('[LootSpawner] Player hit by poison:', pl.name, 'damage:', h.damage);
          try { pl.takeDamage(h.damage); } catch (_) {}
          h.hitWallets.add(key);
          // optional: shrink/consume after first hit
          try { h.node.alpha *= 0.8; } catch (_) {}
        }
      }
    }
  }

  resolveTokenTint(denomination, chain, explicitTint) {
    if (typeof explicitTint === 'number') return explicitTint;
    if (denomination != null) {
      const denomTint = DENOMINATION_TINTS[denomination];
      if (denomTint) return denomTint;
    }
    if (chain && CHAIN_TINTS[chain]) return CHAIN_TINTS[chain];
    return 0xFCD34D;
  }

  spawnTokenChestAt(x, y, amount, options = {}) {
    try {
      const chest = new Chest(this.scene, x, y, amount, options);
      this.chests.push(chest);
      // Note: chests are NOT added to worldPickups or regular loot - they're handled separately
      return chest;
    } catch (_) {
      return null;
    }
  }

  spawnPrizePoolTokens(tokens = []) {
    const bounds = this.scene.gameManager?.spawnBoundsInitial || this.scene.gameManager?.worldBounds || this.scene.cameras.main.worldView;
    const pad = this.spawnRadiusPadding;

    const randomPoint = () => {
      const x = Phaser.Math.Between(bounds.x + pad, bounds.right - pad);
      const y = Phaser.Math.Between(bounds.y + pad, bounds.bottom - pad);
      return { x, y };
    };

    tokens.forEach((entry) => {
      if (!entry) return;
      const chain = typeof entry.chain === 'string' ? entry.chain.toLowerCase() : null;
      const tokenSymbol = entry.tokenSymbol || this.scene?.tokenSymbol || 'SOULWARS';

      // Ground distribution
      const groundAmount = Number(entry.groundAmount) || 0;
      if (groundAmount > 0) {
        const denominations = Array.isArray(entry.denominations) && entry.denominations.length
          ? entry.denominations
              .map((d) => Number(d))
              .filter((d) => Number.isFinite(d) && d > 0)
              .sort((a, b) => b - a)
          : DEFAULT_DENOMINATIONS;

        const coins = this.splitAmountIntoCoins(groundAmount, denominations);
        coins.forEach((value) => {
          const { x, y } = randomPoint();
          this.spawnTokenAt(x, y, value, {
            tokenSymbol,
            chain,
            denomination: value,
          });
        });
      }

      // Chest distribution
      const chestAmount = Number(entry.chestAmount) || 0;
      const chestCount = Math.max(0, Math.floor(Number(entry.chestCount) || 0));
      if (chestAmount > 0 && chestCount > 0) {
        let remaining = chestAmount;
        const perChest = chestAmount / chestCount;
        for (let i = 0; i < chestCount; i++) {
          const amountForChest = i === chestCount - 1 ? remaining : perChest;
          const { x, y } = randomPoint();
          this.spawnTokenChestAt(x, y, Number(amountForChest.toFixed(4)), {
            tokenSymbol,
            chain,
          });
          remaining -= perChest;
        }
      }
    });
  }

  splitAmountIntoCoins(total, denominations = DEFAULT_DENOMINATIONS) {
    const coins = [];
    if (total <= 0) return coins;
    const sorted = [...denominations].map(Number).filter((d) => Number.isFinite(d) && d > 0).sort((a, b) => b - a);
    const epsilon = 0.0001;
    let remaining = total;

    while (remaining > epsilon) {
      let matched = false;
      for (let i = 0; i < sorted.length; i++) {
        const denom = sorted[i];
        if (denom <= 0) continue;
        if (remaining + epsilon >= denom) {
          coins.push(denom);
          remaining -= denom;
          matched = true;
          break;
        }
      }
      if (!matched) {
        coins.push(Number(remaining.toFixed(4)));
        break;
      }
      if (coins.length > 5000) {
        // Prevent runaway loops for extremely granular configs
        coins.push(Number(Math.max(0, remaining).toFixed(4)));
        break;
      }
    }

    return coins;
  }

  // Get all remaining loot on the battlefield (not yet collected)
  // Returns array of loot entries formatted for server transmission
  getRemainingLoot() {
    const remainingLoot = [];
    
    // Filter out destroyed/inactive loot
    const activeLoot = this.loot.filter(l => l && l.active && l.lootData);
    
    // Group by type for aggregation
    const lootByKey = new Map();
    
    activeLoot.forEach(loot => {
      const data = loot.lootData || {};
      const lootType = data.loot_type || 'item';
      
      // Determine loot category
      let category = 'item';
      if (lootType === 'token') {
        category = 'token';
      } else if (data.type === 'healing' || data.type === 'mana' || 
                 data.type === 'strength' || data.type === 'poison') {
        category = 'potion';
      } else if (data.type === 'gem') {
        category = 'gem';
      }
      
      // Create key for grouping
      let key;
      if (category === 'token') {
        key = `token_${data.tokenSymbol || 'SOULWARS'}_${data.chain || ''}`;
      } else {
        key = `${category}_${data.id || data.themeItemId || ''}`;
      }
      
      // Aggregate quantities
      if (!lootByKey.has(key)) {
        const entry = {
          lootType: category,
          itemId: data.id || null,
          themeItemId: data.themeItemId || data.id || null,
          tokenSymbol: data.tokenSymbol || null,
          chain: data.chain || null,
          amount: 0,
          qty: 0
        };
        lootByKey.set(key, entry);
      }
      
      const entry = lootByKey.get(key);
      if (category === 'token') {
        entry.amount = (entry.amount || 0) + (data.amount || 0);
        entry.qty = entry.amount; // For tokens, qty = amount
      } else {
        entry.qty = (entry.qty || 0) + 1;
        entry.amount = entry.qty; // For items, amount = qty
      }
    });
    
    // Convert map to array
    lootByKey.forEach(entry => {
      // Remove null/undefined fields
      const cleanEntry = {};
      if (entry.lootType) cleanEntry.lootType = entry.lootType;
      if (entry.itemId) cleanEntry.itemId = entry.itemId;
      if (entry.themeItemId) cleanEntry.themeItemId = entry.themeItemId;
      if (entry.tokenSymbol) cleanEntry.tokenSymbol = entry.tokenSymbol;
      if (entry.chain) cleanEntry.chain = entry.chain;
      if (entry.amount !== undefined && entry.amount !== null) cleanEntry.amount = entry.amount;
      if (entry.qty !== undefined && entry.qty !== null) cleanEntry.qty = entry.qty;
      
      remainingLoot.push(cleanEntry);
    });
    
    return remainingLoot;
  }
}


